// src/components/audio/NotesPanel.js
//
// Notes panel for the AudioAudit player pane. Shows all notes attached to
// the currently-playing audio file, sorted by timestamp. Supports:
//
//   - "Add note at <currentTime>" button (top right) → inline form
//   - Click a note's timestamp → seeks the player to that position
//   - Edit (pencil) → row becomes editable inline
//   - Delete (trash) → confirmation, then removes
//   - Tag picker uses the closed TAGS vocabulary from audioNotes.js
//
// Empty state is instructive: tells the reviewer how to capture their first note.

import React, { useState, useRef, useEffect } from 'react';
import { Plus, Edit2, Trash2, Save, X, Clock } from 'lucide-react';
import { TAGS, getTag, formatTimestamp } from './audioNotes';

const NotesPanel = ({
  notes,                 // already-filtered + sorted notes for current path
  currentTime,           // playback position, used for "+ Add at ..."
  onAdd,                 // (text, tag, timestamp_seconds) => Promise
  onUpdate,              // (noteId, patch) => Promise
  onDelete,              // (noteId) => Promise
  onJumpTo,              // (timestamp_seconds) => void
  isDarkMode,
  disabled,              // true when no audio loaded yet
}) => {
  const colors = useColors(isDarkMode);
  const [composerOpen, setComposerOpen] = useState(false);
  const [editingId, setEditingId]       = useState(null);
  const [confirmingDeleteId, setConfirmingDeleteId] = useState(null);

  const handleAdd = async (text, tag) => {
    await onAdd(text, tag, currentTime);
    setComposerOpen(false);
  };

  const handleUpdate = async (noteId, patch) => {
    await onUpdate(noteId, patch);
    setEditingId(null);
  };

  const handleDelete = async (noteId) => {
    await onDelete(noteId);
    setConfirmingDeleteId(null);
  };

  return (
    <div style={{
      background: colors.cellBg,
      border: `1px solid ${colors.cellBdr}`,
      borderRadius: '12px',
      padding: '14px 18px 16px',
      fontFamily: "'Outfit', system-ui, sans-serif",
    }}>
      {/* Header */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: '10px',
        marginBottom: notes.length || composerOpen ? '12px' : '0',
      }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{
            fontSize: '10.5px', fontWeight: 600,
            textTransform: 'uppercase', letterSpacing: '0.1em',
            color: colors.textMuted,
          }}>
            Notes {notes.length > 0 && (
              <span style={{ color: colors.textSec, marginLeft: '6px' }}>
                ({notes.length})
              </span>
            )}
          </div>
        </div>
        <button
          onClick={() => setComposerOpen(true)}
          disabled={disabled || composerOpen}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: '6px',
            padding: '6px 12px',
            background: disabled
              ? 'transparent'
              : `linear-gradient(135deg, ${colors.accentDeep}, ${colors.accent})`,
            color: disabled ? colors.textMuted : '#FFFFFF',
            border: disabled ? `1px solid ${colors.cellBdr}` : 'none',
            borderRadius: '8px',
            fontSize: '12px',
            fontWeight: 600,
            fontFamily: 'inherit',
            cursor: disabled || composerOpen ? 'default' : 'pointer',
            opacity: composerOpen ? 0.5 : 1,
            boxShadow: disabled ? 'none' : `0 2px 8px ${colors.accent}33`,
          }}
        >
          <Plus size={13} />
          Add at {formatTimestamp(currentTime || 0)}
        </button>
      </div>

      {/* Composer (inline) */}
      {composerOpen && (
        <NoteComposer
          colors={colors}
          initialTimestamp={currentTime || 0}
          onSave={handleAdd}
          onCancel={() => setComposerOpen(false)}
        />
      )}

      {/* List */}
      {notes.length === 0 && !composerOpen && (
        <EmptyState colors={colors} disabled={disabled} />
      )}

      {notes.length > 0 && (
        <div style={{
          display: 'flex', flexDirection: 'column', gap: '6px',
          marginTop: composerOpen ? '12px' : '0',
        }}>
          {notes.map((n) => (
            <div key={n.id}>
              {editingId === n.id ? (
                <NoteEditor
                  colors={colors}
                  note={n}
                  onSave={(patch) => handleUpdate(n.id, patch)}
                  onCancel={() => setEditingId(null)}
                />
              ) : (
                <NoteRow
                  colors={colors}
                  note={n}
                  onJump={() => onJumpTo?.(n.timestamp_seconds)}
                  onEdit={() => setEditingId(n.id)}
                  onDelete={() => setConfirmingDeleteId(n.id)}
                  confirming={confirmingDeleteId === n.id}
                  onConfirmDelete={() => handleDelete(n.id)}
                  onCancelDelete={() => setConfirmingDeleteId(null)}
                />
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ─── Empty state ──────────────────────────────────────────────────────────
const EmptyState = ({ colors, disabled }) => (
  <div style={{
    padding: '12px 0 4px',
    fontSize: '12.5px',
    color: colors.textMuted,
    lineHeight: 1.6,
  }}>
    {disabled
      ? 'Select an audio file to leave notes.'
      : (<>No notes yet for this audio. Listen and click <strong style={{ color: colors.textSec }}>+ Add at 0:00</strong> when you find something worth flagging.</>)}
  </div>
);

// ─── Single note row ──────────────────────────────────────────────────────
const NoteRow = ({ colors, note, onJump, onEdit, onDelete, confirming, onConfirmDelete, onCancelDelete }) => {
  const tag = getTag(note.tag);
  return (
    <div style={{
      display: 'flex', alignItems: 'flex-start', gap: '10px',
      padding: '8px 10px',
      background: colors.cellBg2,
      border: `1px solid ${colors.cellBdr}`,
      borderRadius: '8px',
      transition: 'background 0.12s ease',
    }}
      onMouseEnter={(e) => e.currentTarget.style.background = colors.cellBgHover}
      onMouseLeave={(e) => e.currentTarget.style.background = colors.cellBg2}
    >
      {/* Timestamp — clickable to jump player */}
      <button
        onClick={onJump}
        title={`Jump to ${formatTimestamp(note.timestamp_seconds)}`}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: '4px',
          padding: '3px 8px',
          background: colors.btnBg,
          border: `1px solid ${colors.cellBdr}`,
          borderRadius: '5px',
          fontSize: '11.5px',
          color: colors.accent,
          fontFamily: "'JetBrains Mono', ui-monospace, monospace",
          fontVariantNumeric: 'tabular-nums',
          cursor: 'pointer',
          flexShrink: 0,
          marginTop: '1px',
          fontWeight: 600,
        }}
      >
        <Clock size={10} />
        {formatTimestamp(note.timestamp_seconds)}
      </button>

      {/* Tag pill */}
      <span style={{
        flexShrink: 0,
        padding: '3px 8px',
        background: `${tag.color}1A`,
        color: tag.color,
        borderRadius: '999px',
        fontSize: '10px',
        fontWeight: 600,
        textTransform: 'lowercase',
        letterSpacing: '0.02em',
        marginTop: '2px',
      }}>
        {tag.label}
      </span>

      {/* Note text */}
      <div style={{
        flex: 1, minWidth: 0,
        fontSize: '13px',
        color: colors.text,
        lineHeight: 1.5,
        wordBreak: 'break-word',
        marginTop: '1px',
      }}>
        {note.text || <span style={{ color: colors.textMuted, fontStyle: 'italic' }}>(empty)</span>}
      </div>

      {/* Actions */}
      {!confirming ? (
        <div style={{ display: 'flex', gap: '4px', flexShrink: 0 }}>
          <ActionButton colors={colors} onClick={onEdit} title="Edit">
            <Edit2 size={12} />
          </ActionButton>
          <ActionButton colors={colors} onClick={onDelete} title="Delete" danger>
            <Trash2 size={12} />
          </ActionButton>
        </div>
      ) : (
        <div style={{ display: 'flex', gap: '4px', flexShrink: 0, alignItems: 'center' }}>
          <span style={{ fontSize: '11px', color: colors.error, fontWeight: 500 }}>Delete?</span>
          <ActionButton colors={colors} onClick={onConfirmDelete} title="Confirm" danger>
            <Trash2 size={12} />
          </ActionButton>
          <ActionButton colors={colors} onClick={onCancelDelete} title="Cancel">
            <X size={12} />
          </ActionButton>
        </div>
      )}
    </div>
  );
};

// ─── Note composer / editor (shared shape) ────────────────────────────────
const NoteComposer = ({ colors, initialTimestamp, onSave, onCancel }) => (
  <NoteForm
    colors={colors}
    initialText=""
    initialTag="probing"
    initialTimestamp={initialTimestamp}
    onSave={(text, tag) => onSave(text, tag)}
    onCancel={onCancel}
    showTimestamp={true}
  />
);

const NoteEditor = ({ colors, note, onSave, onCancel }) => (
  <NoteForm
    colors={colors}
    initialText={note.text}
    initialTag={note.tag}
    initialTimestamp={note.timestamp_seconds}
    onSave={(text, tag, ts) => onSave({ text, tag, timestamp_seconds: ts })}
    onCancel={onCancel}
    showTimestamp={true}
    allowTimestampEdit={true}
  />
);

const NoteForm = ({ colors, initialText, initialTag, initialTimestamp, onSave, onCancel, showTimestamp, allowTimestampEdit }) => {
  const [text, setText] = useState(initialText);
  const [tag, setTag]   = useState(initialTag);
  const [ts, setTs]     = useState(initialTimestamp);
  const [saving, setSaving] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const submit = async () => {
    if (!text.trim() || saving) return;
    setSaving(true);
    try {
      await onSave(text.trim(), tag, ts);
    } finally {
      setSaving(false);
    }
  };

  const handleKey = (e) => {
    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      submit();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      onCancel();
    }
  };

  return (
    <div style={{
      padding: '10px 12px',
      background: colors.cellBg2,
      border: `1px solid ${colors.accent}55`,
      borderRadius: '8px',
      display: 'flex', flexDirection: 'column', gap: '8px',
    }}>
      <textarea
        ref={inputRef}
        value={text}
        onChange={(e) => setText(e.target.value)}
        onKeyDown={handleKey}
        placeholder="What did you notice?"
        rows={2}
        style={{
          width: '100%',
          padding: '8px 10px',
          background: colors.inputBg,
          border: `1px solid ${colors.cellBdr}`,
          borderRadius: '6px',
          color: colors.text,
          fontFamily: 'inherit',
          fontSize: '13px',
          resize: 'vertical',
          outline: 'none',
          boxSizing: 'border-box',
          lineHeight: 1.5,
        }}
      />
      <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
        {/* Tag dropdown */}
        <select
          value={tag}
          onChange={(e) => setTag(e.target.value)}
          style={{
            padding: '5px 8px',
            background: colors.inputBg,
            border: `1px solid ${colors.cellBdr}`,
            borderRadius: '6px',
            color: colors.text,
            fontFamily: 'inherit',
            fontSize: '12px',
            cursor: 'pointer',
            outline: 'none',
          }}
        >
          {TAGS.map(t => (
            <option key={t.id} value={t.id}>{t.label}</option>
          ))}
        </select>

        {/* Timestamp display (locked unless editing) */}
        {showTimestamp && (
          <span style={{
            display: 'inline-flex', alignItems: 'center', gap: '4px',
            fontSize: '11.5px',
            color: colors.textMuted,
            fontFamily: "'JetBrains Mono', ui-monospace, monospace",
            fontVariantNumeric: 'tabular-nums',
          }}>
            <Clock size={11} />
            at {formatTimestamp(ts)}
            {allowTimestampEdit && (
              <button
                onClick={() => {
                  const next = window.prompt(`New timestamp (seconds, currently ${ts})`, String(ts));
                  if (next != null) {
                    const parsed = parseInt(next, 10);
                    if (!Number.isNaN(parsed) && parsed >= 0) setTs(parsed);
                  }
                }}
                style={{
                  marginLeft: '4px',
                  padding: '0 4px',
                  background: 'transparent',
                  border: 'none',
                  color: colors.accent,
                  fontFamily: 'inherit',
                  fontSize: '11px',
                  cursor: 'pointer',
                  textDecoration: 'underline',
                }}
                title="Change timestamp"
              >
                edit
              </button>
            )}
          </span>
        )}

        <span style={{ flex: 1 }} />

        {/* Hint */}
        <span style={{
          fontSize: '10px',
          color: colors.textMuted,
        }}>
          <kbd style={kbdStyle(colors)}>Ctrl+Enter</kbd> save · <kbd style={kbdStyle(colors)}>Esc</kbd> cancel
        </span>

        <button
          onClick={onCancel}
          disabled={saving}
          style={{
            padding: '5px 11px',
            background: 'transparent',
            border: `1px solid ${colors.cellBdr}`,
            borderRadius: '6px',
            color: colors.textSec,
            fontFamily: 'inherit',
            fontSize: '11.5px',
            fontWeight: 500,
            cursor: saving ? 'wait' : 'pointer',
          }}
        >
          Cancel
        </button>
        <button
          onClick={submit}
          disabled={!text.trim() || saving}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: '5px',
            padding: '5px 12px',
            background: text.trim() && !saving
              ? `linear-gradient(135deg, ${colors.accentDeep}, ${colors.accent})`
              : colors.btnBg,
            color: text.trim() && !saving ? '#FFFFFF' : colors.textMuted,
            border: 'none',
            borderRadius: '6px',
            fontFamily: 'inherit',
            fontSize: '11.5px',
            fontWeight: 600,
            cursor: text.trim() && !saving ? 'pointer' : 'default',
            boxShadow: text.trim() && !saving ? `0 2px 8px ${colors.accent}33` : 'none',
          }}
        >
          <Save size={11} />
          {saving ? 'Saving...' : 'Save'}
        </button>
      </div>
    </div>
  );
};

const ActionButton = ({ colors, onClick, title, children, danger }) => (
  <button
    onClick={onClick}
    title={title}
    style={{
      width: '24px', height: '24px',
      borderRadius: '5px',
      background: 'transparent',
      border: 'none',
      color: danger ? colors.error : colors.textMuted,
      cursor: 'pointer',
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
      padding: 0,
      transition: 'background 0.12s ease',
    }}
    onMouseEnter={(e) => e.currentTarget.style.background = danger ? `${colors.error}1A` : colors.cellBgHover}
    onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
  >
    {children}
  </button>
);

const kbdStyle = (colors) => ({
  padding: '1px 4px',
  background: colors.btnBg,
  border: `1px solid ${colors.cellBdr}`,
  borderRadius: '3px',
  fontSize: '9.5px',
  fontFamily: "'JetBrains Mono', ui-monospace, monospace",
  color: colors.textSec,
});

const useColors = (isDarkMode) => isDarkMode
  ? {
      text:        '#E7EAF0',
      textSec:     '#B8BDC9',
      textMuted:   '#6B7180',
      cellBg:      'rgba(255,255,255,0.02)',
      cellBg2:     'rgba(255,255,255,0.025)',
      cellBgHover: 'rgba(255,255,255,0.045)',
      cellBdr:     'rgba(255,255,255,0.06)',
      btnBg:       'rgba(255,255,255,0.04)',
      inputBg:     'rgba(0,0,0,0.2)',
      accent:      '#38B6FF',
      accentDeep:  '#1B6BE0',
      error:       '#EF4444',
    }
  : {
      text:        '#15181D',
      textSec:     '#3A3F4A',
      textMuted:   '#6B7180',
      cellBg:      'rgba(0,0,0,0.02)',
      cellBg2:     'rgba(0,0,0,0.015)',
      cellBgHover: 'rgba(0,0,0,0.04)',
      cellBdr:     'rgba(0,0,0,0.07)',
      btnBg:       'rgba(0,0,0,0.03)',
      inputBg:     '#FFFFFF',
      accent:      '#1B6BE0',
      accentDeep:  '#1453B0',
      error:       '#DC2626',
    };

export default NotesPanel;
